import React, { useMemo, useState } from 'react';
import { Button, Col, Collapse, Table } from 'react-bootstrap';
import { FormCol12 } from 'motion-components';
import { FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';

/** Αποτέλεσμα υπολογισμού υλικών συσκευασίας από AI */
export type AiPackagingMaterialsCalculations = {
  vermiculiteUsed?: boolean;
  vermiculiteQuantity?: number | string;
  aeroplastUsed?: boolean;
  aeroplastQuantity?: number | string;
  plasticBagUsed?: boolean;
  plasticBagQuantity?: number | string;
  polyethyleneUsed?: boolean;
  polyethyleneQuantity?: number | string;
  /** Ποια υλικά έχουν ήδη μεταφερθεί στη φόρμα */
  transferredMaterials?: Record<string, boolean>;
};

type PackagingMaterialKey = 'vermiculite' | 'aeroplast' | 'plasticBag' | 'polyethylene';

const PACKAGING_MATERIAL_ROWS: { key: PackagingMaterialKey; label: string }[] = [
  { key: 'vermiculite', label: 'Βερμικουλίτης' },
  { key: 'aeroplast', label: 'Αεροπλαστ' },
  { key: 'plasticBag', label: 'Πλαστική σακούλα' },
  { key: 'polyethylene', label: 'Πολυαιθυλένιο' },
];

type AiPackagingMaterialsCalculationsPanelProps = {
  aiPackagingMaterialsCalculations?: AiPackagingMaterialsCalculations | string | null;
  /** Τρέχουσες τιμές φόρμας για σύγκριση */
  currentValues?: {
    vermiculiteUsed?: boolean;
    vermiculiteQuantity?: string | number;
    aeroplastUsed?: boolean;
    aeroplastQuantity?: string | number;
    plasticBagUsed?: boolean;
    plasticBagQuantity?: string | number;
    polyethyleneUsed?: boolean;
    polyethyleneQuantity?: string | number;
  };
  className?: string;
  onTransferMaterial?: (materialKey: PackagingMaterialKey, calculations: AiPackagingMaterialsCalculations) => void;
};

/** Μετατροπή raw τιμής (string JSON ή αντικείμενο) σε τυποποιημένο αποτέλεσμα AI */
function parseCalculations(
  raw: AiPackagingMaterialsCalculations | string | null | undefined,
): AiPackagingMaterialsCalculations | null {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function formatBoolean(value: boolean | undefined | null): string {
  if (value === true) return 'Ναι';
  if (value === false) return 'Όχι';
  return '—';
}

function formatQuantity(value: number | string | undefined | null): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

export const AiPackagingMaterialsCalculationsPanel: React.FC<AiPackagingMaterialsCalculationsPanelProps> = ({
  aiPackagingMaterialsCalculations,
  currentValues,
  className = '',
  onTransferMaterial,
}) => {
  const [open, setOpen] = useState(false);

  const calculations = useMemo(
    () => parseCalculations(aiPackagingMaterialsCalculations),
    [aiPackagingMaterialsCalculations],
  );

  if (!calculations) return null;

  return (
    <>
      <Col xs="auto" className="d-flex align-items-center">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="ai-packaging-materials-collapse"
        >
          <FaInfoCircle className="me-2" />
          Αποτελέσματα AI (Υλικά Συσκευασίας)
          {open ? <FaChevronUp className="ms-2" /> : <FaChevronDown className="ms-2" />}
        </Button>
      </Col>
      <FormCol12>
        <Collapse in={open}>
          <div id="ai-packaging-materials-collapse" className={`mt-3 ${className}`.trim()}>
            <Table responsive bordered size="sm" className="small mb-0">
              <thead className="table-light">
                <tr>
                  <th>Υλικό</th>
                  <th>Χρήση (AI)</th>
                  <th>Ποσότητα (AI)</th>
                  <th>Τρέχουσα χρήση</th>
                  <th>Τρέχουσα ποσότητα</th>
                  <th>Ενέργεια</th>
                </tr>
              </thead>
              <tbody>
                {PACKAGING_MATERIAL_ROWS.map(({ key, label }) => {
                  const aiUsed = calculations[`${key}Used` as `${PackagingMaterialKey}Used`];
                  const aiQuantity = calculations[`${key}Quantity` as `${PackagingMaterialKey}Quantity`];
                  const currentUsed = currentValues?.[`${key}Used` as `${PackagingMaterialKey}Used`];
                  const currentQuantity =
                    currentValues?.[`${key}Quantity` as `${PackagingMaterialKey}Quantity`];
                  const isTransferred = calculations.transferredMaterials?.[key] === true;

                  return (
                    <tr key={key}>
                      <td>{label}</td>
                      <td>{formatBoolean(aiUsed)}</td>
                      <td>{formatQuantity(aiQuantity)}</td>
                      <td>{formatBoolean(currentUsed)}</td>
                      <td>{formatQuantity(currentQuantity)}</td>
                      <td>
                        {isTransferred ? (
                          <span
                            className="badge rounded-pill bg-success"
                            style={{ fontSize: '100%', fontWeight: 'normal' }}
                          >
                            Μεταφέρθηκε
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="py-0 px-1"
                            title="Μεταφορά χρήσης και ποσότητας AI στα αντίστοιχα πεδία της φόρμας"
                            onClick={() => onTransferMaterial?.(key, calculations)}
                          >
                            Μεταφορά
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Collapse>
      </FormCol12>
    </>
  );
};

export default AiPackagingMaterialsCalculationsPanel;
